/**
 * client/src/components/ui/loading-state.tsx
 * The one labelled loading indicator (DESIGN-SYSTEM §3.8, §7.1 rule 3,
 * §9.1 #8): a neutral breathing pulse beside a visible one-line label.
 * Generic spinners and bare "Loading…" are banned — say what is loading.
 * Neutral grey only (emerald is reserved for success). Reduced motion is
 * honoured app-wide via <MotionConfig reducedMotion="user"> in App.tsx.
 */

import { motion } from "framer-motion";
import { cn } from "@/lib/cn";
import { PULSE_ANIMATE, PULSE_TRANSITION } from "@/lib/breathing-pulse";

export interface LoadingStateProps {
  /** Required one-line description of what is loading ("Checking your sign-in…"). */
  label: string;
  className?: string;
  /** `md` (default) for page/section loads, `sm` for inline/compact slots. */
  size?: "sm" | "md";
}

export function LoadingState({ label, className, size = "md" }: LoadingStateProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("flex items-center justify-center gap-3", className)}
    >
      <motion.div
        aria-hidden="true"
        data-testid="loading-state-pulse"
        className={cn(
          "shrink-0 rounded-full border-2 border-border-default border-t-text-tertiary",
          size === "sm" ? "w-4 h-4" : "w-6 h-6",
        )}
        animate={PULSE_ANIMATE}
        transition={PULSE_TRANSITION}
      />
      <span className="text-sm text-text-secondary">{label}</span>
    </div>
  );
}
